import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GridColumn } from 'src/components/GridWrapper';
import useWindowSize from 'src/hooks/useWindowSize';
import AttributesCell from 'src/sections/catalog_products/table_cell/AttributesCell';
import SearchAttributesFilter from 'src/sections/search_products/table_cell/SearchAttributesFilter';
import { useContextSelector } from 'use-context-selector';
import { SearchProductFiltersContext } from '../../../sections/search_products/SearchProductFiltersContext';
import SearchCatalogCell from '../../../sections/search_products/table_cell/SearchCatalogCell';
import SearchDescriptionCell from '../../../sections/search_products/table_cell/SearchDescriptionCell';
import SearchQtyCell from '../../../sections/search_products/table_cell/SearchQtyCell';
import SearchSwapItem from '../../../sections/search_products/table_cell/SearchSwapItem';

const useSearchResultColumns = () => {
  const { t } = useTranslation();
  const { width } = useWindowSize();
  const isSupplierUser = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.isSupplierUser
  );

  const gridWidth = useMemo(() => Math.max(width, 1200), [width]);

  const setPercentage = useMemo(
    () => (percentage: number) => Math.round((gridWidth / 100) * percentage),
    [gridWidth]
  );

  const columns: GridColumn<Record<string, unknown>>[] = useMemo(
    () => [
      ...((!isSupplierUser
        ? [
            {
              field: 'selected',
              title: '',
              filter: 'boolean',
              menu: false,
              sortable: false,
              width: 40,
            },
          ]
        : []) as GridColumn<Record<string, unknown>>[]),
      ...((!isSupplierUser
        ? [
            {
              field: 'isNoItem',
              title: '',
              menu: false,
              sortable: false,
              width: 40,
              component: SearchSwapItem,
            },
          ]
        : []) as GridColumn<Record<string, unknown>>[]),
      {
        field: 'qty',
        title: t('Qty'),
        sortable: false,
        filterable: false,
        menu: false,
        width: setPercentage(7),
        show: true,
        component: SearchQtyCell,
      },
      {
        field: 'attributes',
        title: t('Attributes'),
        filter: 'text',
        sortable: false,
        menu: true,
        width: setPercentage(9),
        show: true,
        component: AttributesCell,
        columnMenu: SearchAttributesFilter,
      },
      {
        field: 'itemNum',
        title: t('Item #'),
        filter: 'text',
        sortable: true,
        menu: true,
        width: setPercentage(8),
        show: true,
      },
      {
        field: 'brandName',
        title: t('Brand Name'),
        filter: 'text',
        sortable: true,
        menu: true,
        width: setPercentage(9),
        show: true,
      },
      {
        field: 'description',
        title: t('Description'),
        filter: 'text',
        sortable: true,
        menu: true,
        width: setPercentage(16),
        show: true,
        component: SearchDescriptionCell,
      },
      {
        field: 'catalogName',
        title: t('Catalog'),
        filter: 'text',
        sortable: true,
        menu: true,
        width: setPercentage(10),
        show: true,
        component: SearchCatalogCell,
      },
      {
        field: 'supplierName',
        title: t('Supplier'),
        filter: 'text',
        sortable: true,
        menu: true,
        width: setPercentage(9),
        show: true,
      },
      {
        field: 'uom',
        title: t('UOM'),
        filter: 'text',
        sortable: true,
        menu: true,
        width: setPercentage(7),
        show: true,
      },
      {
        field: 'casePack',
        title: t('Case Pack'),
        filter: 'text',
        sortable: true,
        menu: true,
        width: setPercentage(7),
        show: true,
      },
      {
        field: 'manufacturerNumber',
        title: t('Mfg #'),
        filter: 'text',
        sortable: true,
        menu: true,
        width: setPercentage(7),
        show: true,
      },
      {
        field: 'price',
        title: t('Price'),
        filter: 'numeric',
        format: '{0:c2}',
        filterFormat: 'c',
        dataType: 'dollar',
        sortable: true,
        menu: true,
        width: setPercentage(7),
        show: true,
      },
      {
        field: 'weight',
        title: t('Wt.'),
        filter: 'text',
        dataType: 'string',
        sortable: true,
        menu: true,
        width: setPercentage(7),
        show: true,
      },
    ],
    [isSupplierUser, setPercentage, t]
  );

  return columns;
};

export default useSearchResultColumns;
